import React from 'react';
import { TbArrowRight, TbArrowsSplit } from "react-icons/tb";
import { VscArrowBoth } from "react-icons/vsc";
import { Auction, Pokemon } from '../../../types';
import HoverTip from '../../../shared/components/HoverTip';
import { getTipMessagesEnabled } from '../../../shared/utils/tipMessages';
import { getPokemonDexUrl } from '../../../shared/utils/pokemonDexUrl';
import './CurrentPokemonPanel.scss';

interface CurrentPokemonPanelProps {
  current_auction: Auction;
  all_pokemon: Pokemon[];
  onToggleEgg?: (id: number | null) => void;
}

const EVOLUTION_METHOD_TOOLTIPS: Record<string, string> = {
  Guru: 'An NPC in Slateport City can evolve this Pokémon early after you earn 2 badges',
  Oracle: 'An NPC in Slateport City can evolve this Pokémon early after you earn 4 badges',
};

const SLATEPORT_ITEM_METHOD_TIPS: Record<string, string> = {
  'Linking Cord': 'Available for purchase from the Slateport Market after you earn 6 badges',
  'Dragon Scale': 'Available for purchase from the Slateport Market after you earn 4 badges',
  'Metal Coat': 'Available for purchase from the Slateport Market after you earn 4 badges',
  "King's Rock": 'Available for purchase from the Slateport Market after you earn 4 badges',
};

const SLATEPORT_BADGES_4_TIP = 'Available for purchase from the Slateport Market after you earn 4 badges';

function getWholeMethodTip(method: string): string | null {
  const trimmed = method.trim();
  const itemTip = SLATEPORT_ITEM_METHOD_TIPS[trimmed];
  if (itemTip) return itemTip;
  if (trimmed.endsWith('Stone')) return SLATEPORT_BADGES_4_TIP;
  return null;
}

const EvolutionMethodWord: React.FC<{ text: string; tipText: string; tipsEnabled: boolean }> = ({ text, tipText, tipsEnabled }) => {
  const wordRef = React.useRef<HTMLSpanElement>(null);
  const [hover, setHover] = React.useState(false);

  return (
    <span
      ref={wordRef}
      className={tipsEnabled ? 'evo-method-tooltip' : ''}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {text}
      {hover && (
        <HoverTip text={tipText} color="#6366f1" anchorRef={wordRef} hover={hover} />
      )}
    </span>
  );
};

const EvolutionMethodLabel: React.FC<{ method: string | undefined }> = ({ method }) => {
  const [tipsEnabled, setTipsEnabled] = React.useState(getTipMessagesEnabled);

  React.useEffect(() => {
    const handleSettingsChanged = () => setTipsEnabled(getTipMessagesEnabled());
    window.addEventListener('eb-settings-changed', handleSettingsChanged);
    window.addEventListener('storage', handleSettingsChanged);
    return () => {
      window.removeEventListener('eb-settings-changed', handleSettingsChanged);
      window.removeEventListener('storage', handleSettingsChanged);
    };
  }, []);

  if (!method) return null;

  const wholeTip = getWholeMethodTip(method);
  if (wholeTip) {
    return (
      <span className="evo-method-label-horizontal">
        <EvolutionMethodWord text={method.trim()} tipText={wholeTip} tipsEnabled={tipsEnabled} />
      </span>
    );
  }

  const keywords = Object.keys(EVOLUTION_METHOD_TOOLTIPS);
  const pattern = new RegExp(`(${keywords.join('|')})`, 'g');
  const parts = method.split(pattern);

  return (
    <span className="evo-method-label-horizontal">
      {parts.map((part, i) => {
        const tipText = EVOLUTION_METHOD_TOOLTIPS[part];
        if (tipText) {
          return <EvolutionMethodWord key={i} text={part} tipText={tipText} tipsEnabled={tipsEnabled} />;
        }
        return part;
      })}
    </span>
  );
};


// Helper to build the evolution tree for a given Pokémon
interface EvoNode {
  pokemon: Pokemon;
  children: EvoNode[];
  methodFromParent?: string;
  isMega?: boolean;
}

const getPokemonNodeKey = (pokemon: Pokemon) => {
  return `${pokemon.pokedex_id ?? pokemon.id ?? pokemon.name}-${pokemon.form ?? ''}`;
};

const getStatColorClass = (value: number) => {
  if (value <= 30) return 'stat-bar-red';
  if (value <= 50) return 'stat-bar-orange';
  if (value <= 70) return 'stat-bar-yellow';
  if (value <= 100) return 'stat-bar-light-green';
  if (value <= 150) return 'stat-bar-dark-green';
  return 'stat-bar-light-blue';
};

const getStatWidth = (value: number) => {
  const max = 150;
  return `${Math.min(100, (value / max) * 100)}%`;
};

const getTypeIconSrc = (type: string) => {
  const formattedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  return `/TypeIcons/${formattedType}IC_SV.png`;
};

const getBulbapediaAbilityUrl = (abilityName: string) => {
  const formattedName = abilityName.replace(/ /g, '_');
  return `https://bulbapedia.bulbagarden.net/wiki/${formattedName}_(Ability)`;
};

const getBulbapediaMoveUrl = (moveName: string) => {
  const formattedName = moveName.replace(/ /g, '_');
  return `https://bulbapedia.bulbagarden.net/wiki/${formattedName}_(move)`;
};

function buildEvolutionTree(
  root: Pokemon,
  allPokemon: Pokemon[],
  visited: Set<string> = new Set()
): EvoNode {
  // Unique key for a Pokémon (pokedex_id + form)
  const key = `${root.pokedex_id ?? root.id}-${root.form ?? ''}`;

  if (visited.has(key)) {
    return { pokemon: root, children: [] };
  }

  visited.add(key);

  // Find all Pokémon that evolve from this one (children)
  const children = allPokemon
    .filter(
      (p) => {
        const matches =
          p.evolves_from_id?.toString() === (root.pokedex_id ?? root.id).toString() &&
          (p.evolves_from_form ?? '') === (root.form ?? '');

        return matches;
      }
    )
      .sort((a, b) => (a.pokedex_id ?? a.id ?? 0) - (b.pokedex_id ?? b.id ?? 0))
    .map((child) => {
      const isMega = child.form === 'Mega';
      const childNode = buildEvolutionTree(child, allPokemon, visited);

      return {
        ...childNode,
        methodFromParent: child.evolution_method,
        isMega,
      };
    });

  return { pokemon: root, children };
}


// Render the evolution tree recursively (horizontal)
interface EvolutionTreeProps {
  node: EvoNode;
  activePokemonKey: string;
  onSelectPokemon: (pokemon: Pokemon) => void;
}

const EvolutionTree: React.FC<EvolutionTreeProps> = ({ node, activePokemonKey, onSelectPokemon }) => {
  // Use evolutions folder for all images
  const getImageSrc = (pokemon: Pokemon) => {
    const nameForImg = pokemon.name.includes('(') 
      ? pokemon.name.match(/\(([^)]+)\)/)?.[1] || pokemon.name 
      : pokemon.name;

    if (nameForImg.startsWith('Basculegion-') && (pokemon.form === 'M' || pokemon.form === 'F')) {
      const gender = pokemon.form === 'M' ? 'Male' : 'Female';
      return `/evolutions/Basculegion-${gender}.png`;
    }

    if (pokemon.evolves_from_id && (pokemon.form === 'Alola' || pokemon.form === 'Galar' || pokemon.form === 'Hisui' || pokemon.form === 'Paldea')) {
      return `/evolutions/${nameForImg}-${pokemon.form}.png`;
    } else if (pokemon.evolves_from_id && (pokemon.form !== 'Mega' && pokemon.form !== 'Mega X')) {
      return `/evolutions/${nameForImg}.png`;
    } else if (pokemon.form === 'Mega' || (pokemon.form === 'Mega X' && !pokemon.name.includes('Charizard'))) {
      let baseName = nameForImg.startsWith('Mega ')
        ? nameForImg.slice(5)
        : nameForImg;
      return `/evolutions/${baseName}-Mega.png`;
    } else if (nameForImg.includes('Charizard') && pokemon.form === 'Mega X') {
      return `/evolutions/Charizard X-Mega.png`;
    } else {
      return `/baseforms/${nameForImg}.png`;
    }
  };
  // Determine if this is a baseform or evolution
  const isBaseform = !node.pokemon.evolves_from_id;
  const isSelected = getPokemonNodeKey(node.pokemon) === activePokemonKey;

  return (
    <div className="evo-tree-node-horizontal">
      <div
        className={`evo-pokemon-block-horizontal ${isBaseform ? 'baseform' : ''} ${isSelected ? 'active-evo-selected' : ''}`}
        onClick={() => onSelectPokemon(node.pokemon)}
        title={`Click to view details for ${node.pokemon.name}`}
      >
        <div className="evo-pokemon-name">{node.pokemon.name}</div>
        <div className="evo-img-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
          <img
            src={getImageSrc(node.pokemon)}
            alt={node.pokemon.name}
            title={`Click to view details for ${node.pokemon.name}`}
            className={isBaseform ? "evo-pokemon-img-baseform" : "evo-pokemon-img-evolution"}
          />
          <a
            href={getPokemonDexUrl(node.pokemon.name, node.pokemon.form)}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${node.pokemon.name} in Pokédex (new tab)`}
            className="evo-dex-link"
            onClick={(e) => e.stopPropagation()}
          >
            ↗
          </a>
        </div>
        <div className="pokemon-type-ability">
          {node.pokemon.type1 && (
            <img
              src={getTypeIconSrc(node.pokemon.type1)}
              alt={node.pokemon.type1}
              className={`type-badge type-badge-${node.pokemon.type1.toLowerCase()}`}
            />
          )}
          {node.pokemon.type2 && (
            <img
              src={getTypeIconSrc(node.pokemon.type2)}
              alt={node.pokemon.type2}
              className={`type-badge type-badge-${node.pokemon.type2.toLowerCase()}`}
            />
          )}
        </div>
        <div className="pokemon-stats">
          {!isBaseform && (
            <>
              <div className="evo-pokemon-ability">
                {node.pokemon.ability1 && (
                  <div
                    className="ability-name clickable"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(getBulbapediaAbilityUrl(node.pokemon.ability1!), '_blank');
                    }}
                  >
                    {node.pokemon.ability1}
                  </div>
                )}
                {node.pokemon.ability2 && (
                  <div
                    className="ability-name clickable"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(getBulbapediaAbilityUrl(node.pokemon.ability2!), '_blank');
                    }}
                  >
                    {node.pokemon.ability2}
                  </div>
                )}
                {node.pokemon.hidden_ability && (
                  <div
                    className="ability-name clickable"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(getBulbapediaAbilityUrl(node.pokemon.hidden_ability!), '_blank');
                    }}
                  >
                    {node.pokemon.hidden_ability} (H)
                  </div>
                )}
              </div>
              {node.pokemon.stats && (
                <>
                  <div className="stat-row">
                    <span className="stat-label">HP</span>
                    <span className="stat-value">{node.pokemon.stats.hp}</span>
                    <div
                      className="stat-bar"
                      style={{ width: getStatWidth(node.pokemon.stats.hp) }}
                    >
                      <div
                        className={`stat-bar-fill ${getStatColorClass(node.pokemon.stats.hp)}`}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Atk</span>
                    <span className="stat-value">{node.pokemon.stats.attack}</span>
                    <div
                      className="stat-bar"
                      style={{ width: getStatWidth(node.pokemon.stats.attack) }}
                    >
                      <div
                        className={`stat-bar-fill ${getStatColorClass(node.pokemon.stats.attack)}`}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Def</span>
                    <span className="stat-value">{node.pokemon.stats.defense}</span>
                    <div
                      className="stat-bar"
                      style={{ width: getStatWidth(node.pokemon.stats.defense) }}
                    >
                      <div
                        className={`stat-bar-fill ${getStatColorClass(node.pokemon.stats.defense)}`}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">SpA</span>
                    <span className="stat-value">
                      {node.pokemon.stats.sp_attack ?? node.pokemon.stats.specialAttack}
                    </span>
                    <div
                      className="stat-bar"
                      style={{
                        width: getStatWidth(
                          node.pokemon.stats.sp_attack ?? node.pokemon.stats.specialAttack
                        ),
                      }}
                    >
                      <div
                        className={`stat-bar-fill ${getStatColorClass(
                          node.pokemon.stats.sp_attack ?? node.pokemon.stats.specialAttack
                        )}`}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">SpD</span>
                    <span className="stat-value">
                      {node.pokemon.stats.sp_defense ?? node.pokemon.stats.specialDefense}
                    </span>
                    <div
                      className="stat-bar"
                      style={{
                        width: getStatWidth(
                          node.pokemon.stats.sp_defense ?? node.pokemon.stats.specialDefense
                        ),
                      }}
                    >
                      <div
                        className={`stat-bar-fill ${getStatColorClass(
                          node.pokemon.stats.sp_defense ?? node.pokemon.stats.specialDefense
                        )}`}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Spe</span>
                    <span className="stat-value">{node.pokemon.stats.speed}</span>
                    <div
                      className="stat-bar"
                      style={{ width: getStatWidth(node.pokemon.stats.speed) }}
                    >
                      <div
                        className={`stat-bar-fill ${getStatColorClass(node.pokemon.stats.speed)}`}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      {node.children.length > 0 && (
        <div className="evo-children-block-horizontal">
          {node.children.map((child) => (
            <React.Fragment key={getPokemonNodeKey(child.pokemon)}>
              <div className="evo-arrow-block-horizontal">
                <div className="evo-arrow-container-horizontal">
                  {/* Use react-icons for arrows */}
                  {node.children.length > 1 ? (
                    <TbArrowsSplit size={32} color="#888" />
                  ) : child.isMega ? (
                    <VscArrowBoth size={32} color="#888" />
                  ) : (
                    <TbArrowRight size={32} color="#888" />
                  )}
                  <EvolutionMethodLabel method={child.methodFromParent} />
                </div>
                <EvolutionTree
                  node={child}
                  activePokemonKey={activePokemonKey}
                  onSelectPokemon={onSelectPokemon}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const CurrentPokemonPanel: React.FC<CurrentPokemonPanelProps> = ({ current_auction, all_pokemon, onToggleEgg }) => {
  const basePokemon: Pokemon = current_auction.pokemon;
  const [activePokemon, setActivePokemon] = React.useState<Pokemon>(basePokemon);
  const [showTipModal, setShowTipModal] = React.useState(false);

  React.useEffect(() => {
    setActivePokemon(current_auction.pokemon);
  }, [current_auction.pokemon]);

  const activePokemonKey = getPokemonNodeKey(activePokemon);
  const basePokemonKey = getPokemonNodeKey(basePokemon);
  const isViewingEvolution = activePokemonKey !== basePokemonKey;

  const isEgg = activePokemon.name.trim().toLowerCase() === 'egg' || (activePokemon as any).isRevealedEgg;
  const isUnrevealedEgg = activePokemon.name.trim().toLowerCase() === 'egg';

  // Build the evolution tree for the current Pokémon family starting from root
  const treeRoot = React.useMemo(() => {
    if ((basePokemon as any).isRevealedEgg) {
      // Find the base version of the revealed pokemon to get the full tree
      const targetName = basePokemon.name.match(/\(([^)]+)\)/)?.[1];
      return all_pokemon.find(p => p.name === targetName && !p.evolves_from_id) || basePokemon;
    }
    let curr = basePokemon;
    while (curr.evolves_from_id) {
      const parent = all_pokemon.find(
        (p) =>
          (p.pokedex_id ?? p.id)?.toString() === curr.evolves_from_id?.toString() &&
          (p.form ?? '') === (curr.evolves_from_form ?? '')
      );
      if (!parent || parent === curr) break;
      curr = parent;
    }
    return curr;
  }, [basePokemon, all_pokemon]);

  const evoTree = buildEvolutionTree(treeRoot, all_pokemon);
  const keyMoves = ((activePokemon.key_moves && activePokemon.key_moves.length > 0)
    ? activePokemon.key_moves
    : (basePokemon.key_moves ?? [])
  ).slice().sort((a, b) => a.display_order - b.display_order);

  const activeStats = activePokemon.stats;
  const bst = activeStats
    ? (activeStats.hp ?? 0) +
      (activeStats.attack ?? 0) +
      (activeStats.defense ?? 0) +
      (activeStats.sp_attack ?? activeStats.specialAttack ?? 0) +
      (activeStats.sp_defense ?? activeStats.specialDefense ?? 0) +
      (activeStats.speed ?? 0)
    : 0;

  const formatLearnMethod = (method?: string) => {
    if (!method) return '';
    // Split on slash, trim, replace underscores, and join with comma
    return method
      .split('/')
      .map((m) => m.replace(/_/g, ' ').trim())
      .join('/');
  };

  return (
    <div className="auction-current-pokemon-box" style={{ position: 'relative' }}>
      {isEgg && onToggleEgg && (
        <select 
          className="egg-reveal-select"
          value={(basePokemon as any).isRevealedEgg ? String(basePokemon.pokedex_id ?? basePokemon.id) : ""}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onChange={(e) => {
            const val = e.target.value === "" ? null : parseInt(e.target.value);
            onToggleEgg(val);
          }}
          style={{
            position: 'absolute',
            top: '8px',
            right: '44px',
            zIndex: 100,
            background: 'rgba(30, 32, 35, 0.9)',
            color: '#f1f1f1',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '4px',
            cursor: 'pointer',
            padding: '4px 8px',
            fontSize: '0.95rem',
            outline: 'none'
          }}
        >
          <option value="">Egg</option>
          <option value="298">Azurill</option>
          <option value="173">Cleffa</option>
          <option value="174">Igglybuff</option>
          <option value="175">Togepi</option>
        </select>
      )}
      <div className="pokemon-left-column">
        <div className="pokemon-header">
          <div className="pokemon-left" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <a
                href={getPokemonDexUrl(activePokemon.name, activePokemon.form)}
                target="_blank"
                rel="noopener noreferrer"
                className="pokemon-name-link"
                title={`Open ${activePokemon.name} in Pokédex (new tab)`}
                style={{ textDecoration: 'none', color: 'inherit', display: 'inline-flex', alignItems: 'center' }}
              >
                <h2 className="pokemon-name" style={{ marginRight: 6 }}>
                  {activePokemon.name} <span style={{ fontSize: '0.7em', opacity: 0.6 }}>↗</span>
                </h2>
              </a>
              {activePokemon.description && (
                <button
                  className="pokemon-tip-btn"
                  title="Show tip"
                  onClick={() => setShowTipModal(true)}
                  style={{ marginLeft: 4 }}
                >
                  <span role="img" aria-label="tip">★</span>
                </button>
              )}
            </div>

            {isViewingEvolution && (
              <div className="evo-active-banner">
                <span className="evo-active-tag">Viewing Evolution</span>
                <button
                  type="button"
                  className="evo-revert-btn"
                  onClick={() => setActivePokemon(basePokemon)}
                  title={`Return to base form (${basePokemon.name})`}
                >
                  ↩ Return to {basePokemon.name}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="pokemon-types-badges" style={{ display: 'flex', gap: '6px', paddingLeft: '1.25rem', marginTop: '2px' }}>
          {activePokemon.type1 && (
            <img
              src={getTypeIconSrc(activePokemon.type1)}
              alt={activePokemon.type1}
              className={`type-badge type-badge-${activePokemon.type1.toLowerCase()}`}
            />
          )}
          {activePokemon.type2 && (
            <img
              src={getTypeIconSrc(activePokemon.type2)}
              alt={activePokemon.type2}
              className={`type-badge type-badge-${activePokemon.type2.toLowerCase()}`}
            />
          )}
        </div>

        <div className="pokemon-type-ability">
          <div className="ability-text">
            {activePokemon.ability1 && (
              <div
                className="ability-name clickable"
                onClick={() => window.open(getBulbapediaAbilityUrl(activePokemon.ability1!), '_blank')}
              >
                {activePokemon.ability1}
              </div>
            )}
            {activePokemon.ability2 && (
              <div
                className="ability-name clickable"
                onClick={() => window.open(getBulbapediaAbilityUrl(activePokemon.ability2!), '_blank')}
              >
                {activePokemon.ability2}
              </div>
            )}
            {activePokemon.hidden_ability && (
              <div
                className="ability-name clickable"
                onClick={() => window.open(getBulbapediaAbilityUrl(activePokemon.hidden_ability!), '_blank')}
              >
                {activePokemon.hidden_ability} (H)
              </div>
            )}
          </div>
        </div>

        <div className="pokemon-stats">
          {activeStats && !isUnrevealedEgg && (
            <>
              <div
                className="stat-bst-row"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingRight: '1rem',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#94a3b8',
                  marginBottom: '4px'
                }}
              >
                <span>Base Stats</span>
                <span style={{ color: '#38bdf8' }}>BST: {bst}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">HP</span>
                <span className="stat-value">{activeStats.hp}</span>
                <div
                  className="stat-bar"
                  style={{ width: getStatWidth(activeStats.hp) }}
                >
                  <div
                    className={`stat-bar-fill ${getStatColorClass(activeStats.hp)}`}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className="stat-row">
                <span className="stat-label">Atk</span>
                <span className="stat-value">{activeStats.attack}</span>
                <div
                  className="stat-bar"
                  style={{ width: getStatWidth(activeStats.attack) }}
                >
                  <div
                    className={`stat-bar-fill ${getStatColorClass(activeStats.attack)}`}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className="stat-row">
                <span className="stat-label">Def</span>
                <span className="stat-value">{activeStats.defense}</span>
                <div
                  className="stat-bar"
                  style={{ width: getStatWidth(activeStats.defense) }}
                >
                  <div
                    className={`stat-bar-fill ${getStatColorClass(activeStats.defense)}`}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className="stat-row">
                <span className="stat-label">SpA</span>
                <span className="stat-value">
                  {activeStats.sp_attack ?? activeStats.specialAttack}
                </span>
                <div
                  className="stat-bar"
                  style={{
                    width: getStatWidth(
                      activeStats.sp_attack ?? activeStats.specialAttack
                    ),
                  }}
                >
                  <div
                    className={`stat-bar-fill ${getStatColorClass(
                      activeStats.sp_attack ?? activeStats.specialAttack
                    )}`}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className="stat-row">
                <span className="stat-label">SpD</span>
                <span className="stat-value">
                  {activeStats.sp_defense ?? activeStats.specialDefense}
                </span>
                <div
                  className="stat-bar"
                  style={{
                    width: getStatWidth(
                      activeStats.sp_defense ?? activeStats.specialDefense
                    ),
                  }}
                >
                  <div
                    className={`stat-bar-fill ${getStatColorClass(
                      activeStats.sp_defense ?? activeStats.specialDefense
                    )}`}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className="stat-row">
                <span className="stat-label">Spe</span>
                <span className="stat-value">{activeStats.speed}</span>
                <div
                  className="stat-bar"
                  style={{ width: getStatWidth(activeStats.speed) }}
                >
                  <div
                    className={`stat-bar-fill ${getStatColorClass(activeStats.speed)}`}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {keyMoves.length > 0 && (
          <div className="pokemon-key-moves">
            <div className="key-moves-title">Key Moves:</div>
            <div className="key-moves-list">
              {keyMoves.map((move, index) => (
                <div
                  className="key-move-row"
                  key={`${move.move_name}-${index}`}
                >
                  {move.species ? (
                    <>
                      <img
                        src={`/MiniIcons/${move.species.toLowerCase()}.png`}
                        alt={move.species}
                        className="key-move-species-img"
                        style={{ maxWidth: 20, maxHeight: 20, width: 'auto', height: 'auto', marginRight: 6, verticalAlign: 'middle' }}
                      />
                      <span
                        className="move-name clickable"
                        onClick={() => window.open(getBulbapediaMoveUrl(move.move_name), '_blank')}
                      >
                        {move.move_name}
                      </span>{" "}
                      ({formatLearnMethod(move.learn_method)})
                    </>
                  ) : (
                    <>
                      <span
                        className="move-name clickable"
                        onClick={() => window.open(getBulbapediaMoveUrl(move.move_name), '_blank')}
                      >
                        {move.move_name}
                      </span>{" "}
                      ({formatLearnMethod(move.learn_method)})
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="pokemon-right-column">
        <div className="pokemon-evolution-tree-section">
          <EvolutionTree
            key={getPokemonNodeKey(treeRoot)}
            node={evoTree}
            activePokemonKey={activePokemonKey}
            onSelectPokemon={setActivePokemon}
          />
        </div>
      </div>

      {/* Tip Modal */}
      {showTipModal && (activePokemon.description || basePokemon.description) && (
        <div className="pokemon-tip-modal-overlay" onClick={() => setShowTipModal(false)}>
          <div className="pokemon-tip-modal" onClick={e => e.stopPropagation()}>
            <div className="pokemon-tip-modal-header">
              <button className="pokemon-tip-modal-close" onClick={() => setShowTipModal(false)}>&times;</button>
            </div>
            <div className="pokemon-tip-modal-content">
              {(() => {
                const desc = activePokemon.description || basePokemon.description || '';
                const lines = desc.split(/\\n|\n/);
                return (
                  <>
                    {lines[0] && <h1 style={{ marginTop: 0 }}>{lines[0]}</h1>}
                    {lines.slice(1).map((line: string, idx: number) => (
                      <React.Fragment key={idx}>
                        {line}
                        {idx < lines.length - 2 && <br />}
                      </React.Fragment>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentPokemonPanel;
